import { useClient } from '@cortex/react-query';
import type { Workflow, WorkflowVersion } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { formatDateTime, formatRelative } from '../../../lib/format';
import { useWorkflowEditor } from '../WorkflowEditorContext';

const cellLabel = { width: 140, color: 'var(--text-secondary)', verticalAlign: 'top' as const };

const descriptionMaxLength = 1000;

/**
 * Summary of the workflow's top-level metadata. Lives in the right panel so
 * users can glance at it without leaving the canvas. The description is
 * editable inline (PATCH `description`); everything else is read-only.
 */
export function OverviewPanel() {
  const { workflow: w } = useWorkflowEditor();
  const client = useClient();
  // Same key as the Versions tab, so the number comes from the shared cache.
  const versions = useQuery({
    queryKey: ['workflow-versions', w.id],
    queryFn: () => client.http.get(`v1/workflows/${w.id}/versions`).json<WorkflowVersion[]>(),
    staleTime: 60_000,
  });
  const current = versions.data?.find((v) => v.id === w.currentVersionId) ?? null;
  const rows: Array<{ label: string; value: ReactNode }> = [
    { label: 'ID', value: <code className="t-mono">{w.id}</code> },
    { label: 'Type', value: <code className="t-mono">{w.workflowType}</code> },
    { label: 'Description', value: <EditableDescription workflow={w} /> },
    {
      label: 'Current version',
      // The version NUMBER, as the Versions tab names it; the row id stays in the tooltip.
      value: w.currentVersionId ? (
        <span title={`Version id ${w.currentVersionId}`} data-testid="overview-current-version">
          {current ? `v${current.versionNumber}` : versions.isLoading ? '…' : 'v?'}
        </span>
      ) : (
        '—'
      ),
    },
    {
      label: 'Status',
      value: w.disabledAt ? (
        <span className="badge badge--neutral">Disabled</span>
      ) : (
        <span className="badge badge--success">Active</span>
      ),
    },
    {
      label: 'Last run',
      value: w.lastRunAt ? (
        <span title={formatDateTime(w.lastRunAt)}>{formatRelative(w.lastRunAt)}</span>
      ) : (
        <span className="t-muted">Never run</span>
      ),
    },
    { label: 'Created', value: formatDateTime(w.createdAt) },
    { label: 'Updated', value: formatDateTime(w.updatedAt) },
  ];
  return (
    <div style={{ padding: 'var(--sp-3)' }}>
      <table className="table">
        <tbody>
          {rows.map(({ label, value }) => (
            <tr key={label}>
              <td style={cellLabel}>{label}</td>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Inline-editable workflow description. View mode renders the text (or an
 * "Add a description…" affordance when empty) with a pencil; clicking swaps in
 * a multiline textarea. Enter (without Shift) or blur saves; Escape cancels.
 *
 * Save is an optimistic PATCH against `['workflow', id]` — the same query the
 * editor context reads from — mirroring the name-rename pattern in
 * `WorkflowTopBar`, so the panel updates immediately and reverts on error.
 */
function EditableDescription({ workflow }: { workflow: Workflow }) {
  const client = useClient();
  const qc = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(workflow.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const committedRef = useRef(false);

  const current = workflow.description ?? '';

  useEffect(() => {
    if (!isEditing) setDraft(current);
  }, [current, isEditing]);

  const save = useMutation({
    mutationFn: (description: string) =>
      client.http.patch(`v1/workflows/${workflow.id}`, { json: { description } }).json<Workflow>(),
    onMutate: async (description) => {
      await qc.cancelQueries({ queryKey: ['workflow', workflow.id] });
      const previous = qc.getQueryData<Workflow>(['workflow', workflow.id]);
      if (previous) {
        qc.setQueryData<Workflow>(['workflow', workflow.id], { ...previous, description });
      }
      return { previous };
    },
    onError: (_err, _description, context) => {
      if (context?.previous) qc.setQueryData(['workflow', workflow.id], context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['workflow', workflow.id] }).catch(console.error);
      qc.invalidateQueries({ queryKey: ['workflows'] }).catch(console.error);
    },
  });

  function enterEdit() {
    setDraft(current);
    setError(null);
    setIsEditing(true);
    committedRef.current = false;
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }, 0);
  }

  function cancel() {
    setDraft(current);
    setError(null);
    setIsEditing(false);
    committedRef.current = true;
  }

  function commit() {
    if (committedRef.current) return;
    const next = draft.trim();
    if (next.length > descriptionMaxLength) {
      setError(`Description must be ${descriptionMaxLength} characters or fewer.`);
      return;
    }
    committedRef.current = true;
    setIsEditing(false);
    if (next !== current) save.mutate(next);
  }

  if (isEditing) {
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
        <textarea
          ref={textareaRef}
          className="textarea"
          rows={3}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          disabled={save.isPending}
          aria-label="Workflow description"
          aria-invalid={error !== null}
          placeholder="Describe what this workflow does…"
          spellCheck={true}
        />
        {error ? <span className="field-error">{error}</span> : null}
      </span>
    );
  }

  return (
    <span
      className="inline-editable"
      role="button"
      tabIndex={0}
      onClick={enterEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          enterEdit();
        }
      }}
      aria-label="Workflow description (click to edit)"
      style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 'var(--sp-1)' }}
    >
      {current ? (
        <span style={{ whiteSpace: 'pre-wrap' }}>{current}</span>
      ) : (
        <span className="t-muted">Add a description…</span>
      )}
      <span className="inline-editable__pencil" aria-hidden="true">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </span>
      {save.isPending ? <span className="inline-editable__pending">Saving…</span> : null}
    </span>
  );
}
